import React, { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Navbar from "../Navbar";
import Footer from "./Footer";
import { toast } from "react-hot-toast";
import SEO from "../components/SEO";
import { realEstateListingSchema } from "../components/structuredData";

const BACKEND_URL = "https://backend-ged-immo.onrender.com";
// const BACKEND_URL = "http://localhost:8080";

export default function Details() {
  const { id } = useParams();
  const navigate = useNavigate();

  const homes = useSelector((state) => state.homeReducer.homes || []);
  const rentHomes = useSelector((state) => state.homeReducer.rentHomes || []);
  const [showContact, setShowContact] = useState(false);
  const [sending, setSending] = useState(false);
  const [name, setName] = useState("");
const [email, setEmail] = useState("");
const [number, setNumber] = useState("");


  // REDUX CACHE
  const reduxDetail = useMemo(
    () =>
      homes.find((h) => h._id === id) ||
      rentHomes.find((h) => h._id === id),
    [homes, rentHomes, id]
  );

  // STATES
  const [detail, setDetail] = useState(reduxDetail);
  const [loading, setLoading] = useState(!reduxDetail);
  const [mainImage, setMainImage] = useState("");

  // API FALLBACK
  useEffect(() => {
    if (!reduxDetail) {
      axios
        .get(`${BACKEND_URL}/api/homes/${id}`)
        .then((res) => setDetail(res.data))
        .catch(() => setDetail(null))
        .finally(() => setLoading(false));
    } else {
      setDetail(reduxDetail);
      setLoading(false);
    }
  }, [reduxDetail, id]);

  // IMAGES
  const allImages = useMemo(() => {
    if (!detail) return [];
    return [detail.img, ...(detail.images || [])].filter(Boolean);
  }, [detail]);

  useEffect(() => {
    if (allImages.length > 0) {
      const first = allImages[0];
      setMainImage(first.startsWith("http") ? first : `${BACKEND_URL}/${first}`);
    }
  }, [allImages]);

  const formatImage = (url) => (url?.startsWith("http") ? url : `${BACKEND_URL}/${url}`);
  const yesNo = (v) => (v ? "Oui" : "Non");

  if (loading) return <p style={{ padding: 80, textAlign: "center" }}>Chargement…</p>;
  if (!detail) return <p style={{ padding: 80, textAlign: "center" }}>Bien introuvable</p>;

  const seoImage = detail?.images?.[0] || detail?.img || null;
  const seoTitle = detail
    ? `${detail.label || detail.title || "Bien immobilier"} — ${detail.city || "Abidjan"}`
    : "Détail du bien";
  const seoDesc = detail
    ? `${detail.label || "Bien immobilier"} à ${detail.city || "Abidjan"} — ${detail.pieces || ""} pièces${detail.price ? `, ${Number(detail.price).toLocaleString("fr-FR")} FCFA` : ""}. Disponible chez SCI MAYEDO.`
    : "Découvrez ce bien immobilier proposé par SCI MAYEDO en Côte d'Ivoire.";

  return (
    <div className="details-page">
      <SEO
        title={seoTitle}
        description={seoDesc}
        canonical={`/details/${id}`}
        image={seoImage}
        type="product"
        structuredData={detail ? realEstateListingSchema(detail) : null}
      />
      <Navbar />

      <main className="details-container">
        {/* BOUTON RETOUR */}
        <button className="details-back-btn" onClick={() => navigate(-1)}>
          <i className="fa-solid fa-arrow-left"></i>
          <span>Retour</span>
        </button>

        {/* HEADER */}
        <section className="details-header">
          <h1>{detail.nameHome}</h1>
          <p id="title__header">{detail.addressHome}, {detail.quarter}, {detail.city}</p>
          <div className="status-wrapper">
           {/* 🔴🟢 BADGE STATUT */}
            
          </div>
        </section>

        {/* MAIN */}
        <section className="details-main">
          {/* IMAGES */}
          <div className="image-gallery">
            {mainImage && (
              <div className="main-image">
                <img src={formatImage(mainImage)} alt={detail.nameHome} />
              </div>
            )}

            {allImages.length > 1 && (
              <div className="thumbnails">
                {allImages.map((img, i) => (
                  <img
                    key={i}
                    src={formatImage(img)}
                    alt={`thumb-${i}`}
                    onClick={() => setMainImage(formatImage(img))}
                    className={mainImage === formatImage(img) ? "active" : ""}
                  />
                ))}
              </div>
            )}
          </div>

          {/* INFOS */}
<div className="details-info">
  <h3>Détails du bien</h3>
<ul>
  <li><strong>Référence :</strong> {detail.reference}</li>
  <li><strong>Nom :</strong> {detail.nameHome}</li>
  {/* <li><strong>Type :</strong> {detail.nameHomeType}</li> */}
  <li><strong>Catégorie :</strong> {detail.categorie}</li>
  <li><strong>Nombres de Pièces :</strong> {detail.NmbrePieces}</li>
  <li><strong>Loyer :</strong> {detail.rent?.toLocaleString()} FCFA</li>
  <li><strong>Caution :</strong> {detail.guarantee?.toLocaleString()} FCFA</li>
  {/* <li><strong>Charges :</strong> {detail.charges || "N/A"}</li> */}
  <li><strong>Adresse :</strong> {detail.addressHome}</li>
  <li><strong>Quartier :</strong> {detail.quarter}</li>
  <li><strong>Ville :</strong> {detail.city}</li>
    <span className={`status-badge ${
                  detail.state === "Disponible" ? "available" : "occupied"
                }`}
              >
                {detail.state}
              </span>
</ul>

  <p>{detail.description || "Aucune description fournie"}</p>

  <h4>Équipements & Options</h4>
  <ul>
    {/* HABITATION */}
    {["Appartement", "Villa Duplex",, "Villa Basse", "Villa"].includes(detail.nameHomeType) && (
      <>
        <li>Jardin : {yesNo(detail.jardin)}</li>
        {/* <li>Balcon : {yesNo(detail.balcon)}</li> */}
        <li>Piscine : {yesNo(detail.piscine)}</li>
        <li>Garage : {yesNo(detail.garage)}</li>
        <li>Terrasse : {yesNo(detail.terrasse)}</li>
        {/* <li>Mezzanine : {yesNo(detail.mezanine)}</li> */}
        <li>Service sécurité : {yesNo(detail.serviceSecurite)}</li>
        <li>Ascenseur : {yesNo(detail.ascenseur)}</li>
        {/* <li>Climatisation : {yesNo(detail.climatisation)}</li> */}
        {/* <li>Fibre optique : {yesNo(detail.accesInternet)}</li> */}
        {/* <li>Parking : {yesNo(detail.parking)}</li> */}
      </>
    )}

    {/* BUREAU */}
    {detail.nameHomeType === "Bureau" && (
      <>
        <li>Climatisation : {yesNo(detail.climatisation)}</li>
        <li>Fibre optique : {yesNo(detail.accesInternet)}</li>
        <li>Parking : {yesNo(detail.parking)}</li>
        <li>Ascenseur : {yesNo(detail.ascenseur)}</li>
        <li>Salle de réunion : {yesNo(detail.salleReunion)}</li>
      </>
    )}

    {/* MAGASIN */}
    {detail.nameHomeType === "Magasin" && (
      <>
        <li>Vitrine : {yesNo(detail.vitrine)}</li>
        <li>Stock disponible : {yesNo(detail.stockDisponible)}</li>
        <li>Zone commerciale : {yesNo(detail.zoneCommerciale)}</li>
        <li>Parking : {yesNo(detail.parking)}</li>
      </>
    )}

    {/* ENTREPÔT */}
    {/* {detail.nameHomeType === "Entrepôt" && (
      <>
        <li>Surface : {detail.surfaceEntrepot}</li>
        <li>Parking : {yesNo(detail.parking)}</li>
        <li>Sécurité : {yesNo(detail.serviceSecurite)}</li>
      </>
    )} */}
  </ul>

  <button className="cta-button" onClick={() => setShowContact(true)}>
    Nous contacter
  </button>

  <a
    className="whatsapp-button"
    href={`https://wa.me/2250777880082?text=${encodeURIComponent(
      `Bonjour SCI MAYEDO,\n\nJe suis intéressé par le bien suivant :\n\n🏠 *${detail.nameHome}*\n📍 ${detail.addressHome}, ${detail.quarter}, ${detail.city}\n🛏 ${detail.NmbrePieces} pièce(s)\n💰 ${detail.rent?.toLocaleString()} FCFA / mois\n\nMerci de me recontacter.`
    )}`}
    target="_blank"
    rel="noopener noreferrer"
  >
    <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
    Contacter sur WhatsApp
  </a>
</div>
        </section>
      </main>

      {/* MODAL CONTACT */}


{showContact && (
  <div className="modal-overlay-mail">
    <div className="modal-mail">
      <div className="modal-header">
        <h2>📩 Demande d’information</h2>
        <button className="close-btn" onClick={() => setShowContact(false)}>✕</button>
      </div>

      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (sending) return;

          if (!name || !email || !number) {
            toast.error("⚠️ Veuillez remplir tous les champs !");
            return;
          }

          const message = `Bonjour SCI MAYEDO,

Je suis intéressé par le bien suivant :

Nom du bien : ${detail.nameHome}
Nombre de pièces : ${detail.NmbrePieces}
Adresse : ${detail.addressHome}, ${detail.quarter}, ${detail.city}
Loyer : ${detail.rent?.toLocaleString()} FCFA

Merci de me recontacter.
`;

          const data = {
            name,
            email,
            number,
            message,
            homeId: detail._id,
            homeName: detail.nameHome,
            NmbrePieces: detail.NmbrePieces
          };

          try {
            setSending(true);
            const res = await axios.post(`${BACKEND_URL}/api/contact`, data);

            if (res.data?.success) {
              toast.success("✅ Message envoyé avec succès !");
              setShowContact(false);
              setName("");
              setEmail("");
              setNumber("");
            } else {
              toast.error("❌ Échec de l'envoi. Veuillez réessayer.");
            }
          } catch (err) {
            console.error(err);
            toast.error("❌ Erreur serveur lors de l'envoi du message.");
          } finally {
            setSending(false);
          }
        }}
      >
        <input
          name="name"
          placeholder="Votre nom"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          name="email"
          type="email"
          placeholder="Votre email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <input
          name="number"
          type="text"
          placeholder="Votre numéro"
          required
          value={number}
          onChange={(e) => setNumber(e.target.value)}
        />

        {/* 🔹 Affichage du message final */}
        <textarea
          rows="12"
          value={`Bonjour,

Je suis intéressé par le bien suivant :

Nom du bien : ${detail.nameHome}
Nombre de pièces : ${detail.NmbrePieces}
Adresse : ${detail.addressHome}, ${detail.quarter}, ${detail.city}
Loyer : ${detail.rent?.toLocaleString()} FCFA

Merci de me recontacter.
`}
          readOnly
        />

        <button type="submit" className="submit-btn" disabled={sending}>
          {sending ? "Envoi..." : "Envoyer la demande"}
        </button>
      </form>
    </div>
  </div>
)}

      <Footer />

      {/* STYLES */}
      <style>{`
      #title__header {
      font-weight:bold;
      color:black;
      }
        /* ------------------ IMAGES ------------------ */
        .image-gallery {
          display: flex;
          flex-direction: column;
          gap: 12px;
          margin-bottom: 24px;
        }

        .main-image img {
          width: 100%;
          max-height: 400px;
          object-fit: cover;
          border-radius: 12px;
          transition: transform 0.3s ease;
        }

        .main-image img:hover {
          transform: scale(1.02);
        }

        .thumbnails {
          display: flex;
          gap: 8px;
          overflow-x: auto;
        }

        .thumbnails img {
          width: 80px;
          height: 60px;
          object-fit: cover;
          border-radius: 8px;
          cursor: pointer;
          border: 2px solid transparent;
          transition: border 0.2s ease;
        }

        .thumbnails img.active {
          border-color: rgb(12, 52, 103);
        }

        /* ------------------ CTA ------------------ */
        .cta-button {
          display: block;
          width: 100%;
          padding: 12px 20px;
          background: rgb(12, 52, 103);
          color: #fff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 12px;
          text-align: center;
        }

        .cta-button:hover {
          background: rgb(9, 39, 79);
        }

        /* ------------------ WHATSAPP ------------------ */
        .whatsapp-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 12px 20px;
          background: #25D366;
          color: #fff;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          font-weight: 600;
          margin-top: 10px;
          text-decoration: none;
          font-size: 15px;
          transition: background 0.2s ease, transform 0.15s ease;
          box-sizing: border-box;
        }

        .whatsapp-button:hover {
          background: #1ebe5d;
          color: #fff;
          transform: translateY(-1px);
          text-decoration: none;
        }

        /* ------------------ MODAL ------------------ */
        .modal-overlay-mail {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.65);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
        }

        .modal-mail {
          background: #fff;
          width: 100%;
          max-width: 520px;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.25);
          animation: fadeIn 0.3s ease;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .close-btn {
          border: none;
          background: none;
          font-size: 20px;
          cursor: pointer;
        }

        .modal-mail input,
        .modal-mail textarea {
          width: 100%;
          margin-bottom: 14px;
          padding: 12px;
          border-radius: 8px;
          border: 1px solid #ddd;
          font-size: 14px;
        }

        .modal-mail textarea {
          resize: none;
        }

        .submit-btn {
          width: 100%;
          padding: 12px;
          background: rgb(12, 52, 103);
          color: white;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
        }

        .submit-btn:hover {
          background: rgb(12, 52, 103);
        }

        @keyframes fadeIn {
          from { transform: translateY(10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
          /* ================= STATUS BADGE ================= */

        
// .status-badge {
//   position: absolute;
//   top: 12px;
//   left: 12px;
//   padding: 6px 14px;
//   font-size: 12px;
//   font-weight: 700;
//   border-radius: 999px;
//   text-transform: uppercase;
//   letter-spacing: 0.4px;
//   display: inline-flex;
//   align-items: center;
//   gap: 6px;
//   box-shadow: 0 4px 12px rgba(0,0,0,0.15);
// }
.status-badge {
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* 🟢 DISPONIBLE */
.status-badge.available {
  background: linear-gradient(135deg, #16a34a, #22c55e);
  color: #ffffff;
}

/* 🔴 OCCUPÉE */
.status-badge.occupied {
  background: linear-gradient(135deg, #dc2626, #ef4444);
  color: #ffffff;
}

/* 🔵 Animation point */
.status-badge::before {
  content: "";
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: currentColor;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.4); opacity: 0.6; }
  100% { transform: scale(1); opacity: 1; }
}
  .details-header {
  position: relative;
}

/* ── Bouton retour ── */
.details-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 1.5rem 0 0.5rem;
  padding: 10px 22px;
  background: #fff;
  color: #1B4F9E;
  font-size: 0.9rem;
  font-weight: 700;
  border: 2px solid #1B4F9E;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(27,79,158,0.12);
  transition: all 0.22s ease;
}
.details-back-btn i {
  font-size: 0.85rem;
  transition: transform 0.22s ease;
}
.details-back-btn:hover {
  background: #1B4F9E;
  color: #fff;
  box-shadow: 0 8px 24px rgba(27,79,158,0.25);
  transform: translateY(-2px);
}
.details-back-btn:hover i {
  transform: translateX(-4px);
}
      `}</style>
    </div>
  );
}